import "./app.scss";
import "bootstrap";
import "element-closest";
import "whatwg-fetch";
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { HttpClient, HttpClientConfiguration } from "aurelia-fetch-client";
import { autoinject, PLATFORM } from "aurelia-framework";
import { RouteConfig, Router, RouterConfiguration } from "aurelia-router";
import { UserManager } from "oidc-client-ts";

import Infrastructure from "./infrastructure/infrastructure";
import { appLogger } from "./infrastructure/logging";

dayjs.extend(utc);
dayjs.extend(timezone);
@autoinject
export class App {

    private readonly _userManager: UserManager;

    public router: Router;
    public currentYear: string = "" + new Date().getFullYear();
    public releaseVersion = Infrastructure.releaseVersion;
    public releaseEnvironment = Infrastructure.releaseEnvironment;
    public releaseTimestamp = Infrastructure.releaseTimestamp;
    public buildVersion = Infrastructure.buildVersion;
    public showFooter: boolean;
    public today: Dayjs = dayjs();
    public timeZone: any;

    public constructor(client: HttpClient, userManager: UserManager, router: Router) {
        this._userManager = userManager;
        this.timeZone = dayjs.tz.guess();
        this.configureHttpClient(client);
        this.router = router;
    }

    public configureRouter(config: RouterConfiguration, router: Router) {
        this.router = router;
        let routes = this.configureRoutes();
        config.title = "Axxess Identity";
        config.options.pushState = true;
        config.map(routes);
    }

    public attached() {
        if (this.router.currentInstruction.fragment == "/login") {
            this.showFooter = false;
        } else {
            this.showFooter = true;
        }
    }

    private configureHttpClient(client: HttpClient): void {
        client.configure((config: HttpClientConfiguration) => {
            config
                .withBaseUrl("/api/")
                .withDefaults({
                    credentials: "same-origin",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json",
                        "X-Requesting-App": "Axxess-Identity",
                        "X-Time-Zone" : this.timeZone
                    }
                })
                .withInterceptor({
                    request: async (request: Request) => {
                        appLogger.info(`Requesting ${request.method} ${request.url}`);

                        let user = await this._userManager.getUser();
                        if (user != null) {
                            request.headers.append("Authorization", `${user.token_type} ${user.access_token}`);
                        } else {
                            appLogger.info("User is not logged in, no Authorization set.");
                        }

                        return request; // you can return a modified Request, or you can short-circuit the request by returning a Response
                    },
                    response: async (response: Response) => {
                        appLogger.info(`Received ${response.status} ${response.url}`);
                        return response; // you can return a modified Response
                    }
                });
        });
    }

    private configureRoutes(): RouteConfig[] {
        return [
            { route: [""], name: "index", redirect: "profile" },
            { route: ["login"], name: "login", moduleId: PLATFORM.moduleName("./pages/login/login") },
            { route: ["logout"], name: "logout", moduleId: PLATFORM.moduleName("./pages/logout/logout", "logout") },
            { route: ["slim-logout"], name: "slim-logout", moduleId: PLATFORM.moduleName("./pages/slim-logout/slim-logout", "slim-logout") },
            { route: ["sign-up"], name: "sign-up", moduleId: PLATFORM.moduleName("./pages/sign-up/sign-up", "sign-up") },
            { route: ["sign-up-portal"], name: "sign-up-portal", moduleId: PLATFORM.moduleName("./pages/sign-up-portal/sign-up-portal", "sign-up-portal") },
            { route: ["verify-device"], name: "verify-device", moduleId: PLATFORM.moduleName("./pages/verify-device/verify-device", "verify-device") },
            { route: ["profile"], name: "profile", moduleId: PLATFORM.moduleName("./pages/profile/profile", "profile") },
            { route: ["signin-oidc"], name: "signin-oidc", moduleId: PLATFORM.moduleName("./pages/signin-oidc/signin-oidc", "profile") },
            { route: ["error"], name: "error", moduleId: PLATFORM.moduleName("./pages/error/error", "error") },
            { route: ["access-denied"], name: "access-denied", moduleId: PLATFORM.moduleName("./pages/error/access-denied", "access-denied") },
            { route: ["request-reset"], name: "request-reset", moduleId: PLATFORM.moduleName("./pages/request-reset/request-reset", "request-reset") },
            { route: ["reset-password"], name: "reset-password", moduleId: PLATFORM.moduleName("./pages/reset-password/reset-password", "reset-password") },
            {route:["disable-two-factor-authentication"],name:"disable-two-factor-authentication",moduleId:PLATFORM.moduleName("./pages/disable-two-factor-authentication/disable-two-factor-authentication","disable-two-factor-authentication")},
        ];
    }
}